import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import z from 'zod';
import { DocumentSchema, type DocumentType } from './types';

export const RemoteDocumentSchema = DocumentSchema.and(z.object({ id: z.string() }));
export const GetStorageResponseSchema = z.object({
  resources: z.array(RemoteDocumentSchema),
});

const PostStorageResponseSchema = z.object({
  resource: RemoteDocumentSchema,
  clientId: z.string().optional(),
  id: z.string(),
});
export type PostStorageResponseType = z.infer<typeof PostStorageResponseSchema>;

const PutStorageResponseSchema = z.object({
  resource: RemoteDocumentSchema,
  id: z.string(),
});
export type PutStorageResponseType = z.infer<typeof PutStorageResponseSchema>;

const SuccessUploadReponseSchema = z.object({
  status: z.number().gt(200).lt(300),
  data: z.string().url(),
});

const SuccessDownloadReponseSchema = z.object({
  status: z.number().gt(200).lt(300),
  data: z.string().url(),
});

export default function buildStorageApi(domain: string) {
  return {
    async create(item: DocumentType & { clientId: string }) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document`;
      return PostStorageResponseSchema.parse((await httpClientInterface.send('POST', requestUrl, item)).data);
    },
    async fetch() {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document`;
      return GetStorageResponseSchema.parse((await httpClientInterface.send('GET', requestUrl)).data).resources;
    },
    async update(item: DocumentType & { id: string }) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${item.id}`;
      return PutStorageResponseSchema.parse((await httpClientInterface.send('PUT', requestUrl, item)).data);
    },
    async delete(id: string) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${id}`;
      await httpClientInterface.send('DELETE', requestUrl);
    },

    async upload({
      fileUri,
      documentId,
      name,
      mimeType,
    }: { fileUri: string; documentId: string; name: string; mimeType: string }) {
      const localFile = await fetch(fileUri);
      const fileBlob = await localFile.blob();
      const file = new File([fileBlob], name);
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${documentId}/upload`;
      const uploadUrl = SuccessUploadReponseSchema.parse(await httpClientInterface.send('PUT', requestUrl))?.data;
      // Use native fetch since axios has a polyfill issue on mobile https://github.com/axios/axios/issues/1321.
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });
    },

    async getCdnUrl(id: string, useCdn: boolean) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${id}/download?cdn=${useCdn}`;
      return SuccessDownloadReponseSchema.parse(await httpClientInterface.send('PUT', requestUrl)).data;
    },
  } as const;
}
